/**
 * The ordering PACK — this app's contribution to `ShellProvider` (UIFR-1628).
 *
 * WHAT MOVED, AND WHY IT IS NOT A REWRITE. Before this file the app had no
 * route table at all: the mode was a `useState` inside the framework's
 * `ChameleonShell`, so `/kitchen` was not a URL, a refresh dropped a mounted
 * kitchen display back to whichever mode happened to be first, and the
 * `catchall_document: index.html` the deploy spec has always carried served a
 * single-page app with exactly one page. A pack is the platform's answer to
 * that: it contributes a ROUTE TABLE and the shell derives the menu, the paths
 * and the gating from it.
 *
 * THE TABLE BELOW IS DECLARED, NOT DERIVED. Building it from the framework's
 * own `SHELL_MODE_ORDER` would be shorter and would assert nothing: the two
 * sides would agree by construction.
 *
 * THE VOCABULARY IS THE MANIFEST'S, AND THE APP OWNS IT (ORDR-41). The
 * framework's own mode map named five capabilities paio-auth never issued, so
 * every mode was drawn for nobody. ui-components 0.3.2 lets the host supply the
 * map; `ORDERING_MODE_CAPABILITIES` below is that map, and OrderingShell MUST
 * pass it, because omitted the framework silently falls back to its deprecated
 * default. The customer mode is `null`, ungated: the manifest has no customer
 * role, and a diner arriving by QR holds no grant. So the segments,
 * the order and the capability strings are written out, and
 * `orderingPack.test.ts` asserts the modes still agree with what the installed
 * framework ships and the capabilities with what paio-auth's ordering manifest
 * issues. What the framework DOES contribute is the `ShellMode` type,
 * which makes the record exhaustive: a sixth mode fails `tsc -b` by name rather
 * than going missing.
 *
 * COURTESY, NOT SECURITY. `capability` here decides what is drawn. The server
 * resolves the same set independently on every request and refuses a caller who
 * reached a mode by editing the address bar.
 */

import {
  type ActingNode,
  type ModeCapabilities,
  type ShellMode,
} from "@pirouz-ca/ui-components";
import {
  buildNav,
  deriveNav,
  matchShellRoute,
  type CapabilityCheck,
  type ShellNavItem,
  type ShellPack,
} from "@pirouz-ca/ui-dashboard-shell";

/**
 * Where the shell is mounted. This app IS the shell — there is no console
 * around it and no second product sharing the origin — so it mounts at the
 * root. `buildNav` strips the trailing slash, so `/kitchen` stays `/kitchen`.
 */
export const ORDERING_BASE_PATH = "/";

/** The tab's name before any mode is open. Mirrors `index.html`'s `<title>`. */
export const ORDERING_DEFAULT_TITLE = "Order";

/** One label key per mode. The app's vocabulary, never a framework string. */
export type OrderingLabelKey = `mode.${ShellMode}`;

interface ModeContribution {
  /** The single path segment below the mount that opens this mode. */
  readonly segment: string;
  /** Explicit and stable. Nav order never depends on table position. */
  readonly order: number;
  /**
   * The manifest capability the acting node must hold, or `null` when the mode
   * is ungated. Courtesy, not security.
   */
  readonly capability: string | null;
  /** What this mode is called to the person using it. */
  readonly label: string;
}

/**
 * Exhaustive by construction: `Record<ShellMode, ...>` will not compile with a
 * mode missing. The framework's own presentation order is an ARRAY and its
 * header says so — a mode absent from it compiles, has a capability, has a
 * route, and is reachable by nobody. This record cannot reach that state.
 *
 * ONBOARDING IS LAST AND THAT IS THE FRAMEWORK'S DECISION, MIRRORED. Somebody
 * who both commissions locations and runs one should land on Admin rather than
 * in a wizard they finished last week.
 */
const ORDERING_MODES: Record<ShellMode, ModeContribution> = {
  customer: { segment: "order", order: 10, capability: null, label: "Order" },
  kitchen: { segment: "kitchen", order: 20, capability: "ticket:view", label: "Kitchen" },
  // Staff take orders at a location: the grant is location-scoped, the word is
  // the same one a diner's order is created under server-side.
  staff: { segment: "staff", order: 30, capability: "order:create", label: "Staff" },
  admin: { segment: "admin", order: 40, capability: "menu:author", label: "Admin" },
  onboarding: {
    segment: "onboarding",
    order: 50,
    capability: "merchant:onboard",
    label: "Onboarding",
  },
};

/** The modes this pack contributes, in the order the record declares them. */
export const ORDERING_MODE_NAMES = Object.keys(ORDERING_MODES) as readonly ShellMode[];

/**
 * The map ChameleonShell gates on, read out of the same table as the nav, so
 * the mode bar and the nav model cannot disagree about what is allowed.
 * Module-level and frozen: the shell memoises on its identity.
 */
export const ORDERING_MODE_CAPABILITIES: ModeCapabilities = Object.freeze(
  Object.fromEntries(
    ORDERING_MODE_NAMES.map((mode) => [mode, ORDERING_MODES[mode].capability]),
  ) as Record<ShellMode, string | null>,
);

/**
 * The pack, as it hands itself to `ShellProvider`.
 *
 * Every route carries nav metadata, so `unlinkedRoutes` is empty — there is no
 * screen here that is reached some other way, and the completeness test says so
 * rather than leaving it to be noticed.
 */
export const ORDERING_PACK: ShellPack<ShellMode, OrderingLabelKey> = {
  id: "ordering",
  routes: ORDERING_MODE_NAMES.map((mode) => ({
    name: mode,
    segments: [ORDERING_MODES[mode].segment],
    nav: {
      labelKey: `mode.${mode}` as OrderingLabelKey,
      order: ORDERING_MODES[mode].order,
      capability: ORDERING_MODES[mode].capability,
    },
  })),
};

/** The only pack mounted here. Module-level so the provider's memo holds. */
export const ORDERING_PACKS: readonly ShellPack<ShellMode, OrderingLabelKey>[] = [ORDERING_PACK];

/** The nav model, DERIVED from the table above and resolved against the mount. */
export const ORDERING_NAV: readonly ShellNavItem<ShellMode, OrderingLabelKey>[] = buildNav(
  deriveNav(ORDERING_PACKS),
  ORDERING_BASE_PATH,
);

const ORDERING_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  ORDERING_MODE_NAMES.map((mode) => [`mode.${mode}`, ORDERING_MODES[mode].label]),
);

/**
 * One label key, in this tenant's words.
 *
 * Falls back to the key rather than to an empty string: a menu entry reading
 * `mode.kitchen` is a bug anyone can see and report, and a blank one is a bug
 * that looks like a styling problem.
 */
export function resolveOrderingLabel(labelKey: string): string {
  return ORDERING_LABELS[labelKey] ?? labelKey;
}

/**
 * Does the ACTING node hold this capability?
 *
 * The node, not the user: the same human is a diner at one venue and a manager
 * at another, so the answer has to change when the chooser does.
 */
export function capabilityCheckFor(node: ActingNode | undefined): CapabilityCheck {
  const held = new Set<string>(node === undefined ? [] : node.capabilities);
  return (capability: string): boolean => held.has(capability);
}

/**
 * The mode a pathname names, or `null` when it names none.
 *
 * `null` is not an error: `/` names no mode, and the shell then falls back to
 * the first one the acting node permits. Only a path that names a mode the node
 * lacks is refused, and the framework does that refusing.
 */
export function modeFromPath(path: string): ShellMode | null {
  const match = matchShellRoute(ORDERING_BASE_PATH, path, ORDERING_PACK.routes);
  return match === null ? null : match.name;
}

/**
 * The pathname that opens a mode — read out of the NAV MODEL, not rebuilt from
 * the segment, so the path a link points at and the path `modeFromPath`
 * resolves are the same string by construction.
 */
export function pathForMode(mode: ShellMode): string {
  const item = ORDERING_NAV.find((entry) => entry.name === mode);
  return item === undefined ? ORDERING_BASE_PATH : item.path;
}
